import { useUserStore } from "@/stores/user";
import { unifiedApi } from "@/common";
import { IClientException } from "./api/unifiedPortal/interfaces";
import { BUILD_HASH, BUILD_HASH_DISPLAY_LENGTH, FLAGS } from "@/define";
import { AxiosError } from "axios";
import { IErrorMessage } from "./api/interfaces";

const EVENT_ARRAY = "event_array";
const MAX_EVENTS = 10;
const MAX_EXTRA_LENGTH = 500;
const CLIENT_IDENTIFIER = "VueDawn";

interface eventData {
  ticks: number;
  file: string;
  message: string;
  extra: string;
}

export function logEvent(message: string, file?: string | undefined, value?: unknown) {
  //create event object
  let extra = "";
  if (value instanceof String || typeof value == "string") extra = value as string;
  else if (value instanceof Object) extra = JSON.stringify(value);

  if (extra.length > MAX_EXTRA_LENGTH) {
    //If the extra is too big DO NOT ADD!
    extra = "";
  }
  const ev: eventData = {
    ticks: new Date().getTime(),
    file: file || "",
    message,
    extra,
  };

  //read events from local storage, if any
  let events: eventData[] = [];
  const eventsString = localStorage.getItem(EVENT_ARRAY);
  if (eventsString) events = JSON.parse(eventsString) as eventData[];

  //add the new event
  events.push(ev);
  if (events.length > MAX_EVENTS) events.shift();

  //save the array
  localStorage.setItem(EVENT_ARRAY, JSON.stringify(events));

  //now log to the console
  if (FLAGS.IS_DEBUG) {
    console.log(`${new Date(ev.ticks).toISOString()} | ${ev.file} | ${ev.message}`);
    if (value) console.log(value);
  }
}

interface IErrorObject {
  time: string;
  message: string;
  status: number | undefined;
  pageURL: string;
  apiURL: string;
}

export async function logAxiosException(err: AxiosError) {
  const userStore = useUserStore();
  const user = userStore.currentUser;

  //Build a error object to stringify
  const errorObject: IErrorObject = {
    time: new Date().getTime().toString(),
    message: (err.response?.data as IErrorMessage).message ?? "",
    status: err.response?.status,
    pageURL: window.location.href,
    apiURL: err.request.responseURL ?? "",
  };

  let stackTrace = "";
  if (err.stack) stackTrace = JSON.stringify(err.stack.split("\n"));

  //create clientException message
  const ce: IClientException = {
    clientIdentifier: `${CLIENT_IDENTIFIER} ${BUILD_HASH.substring(0, BUILD_HASH_DISPLAY_LENGTH)}`,
    errorMessage: JSON.stringify(errorObject),
    cause: localStorage.getItem(EVENT_ARRAY) || "",
    stackTrace,
    url: err.request.responseURL ?? "",
    userAgent: "isMobile: " + !localStorage.getItem("isMobile") + " OS: " + localStorage.getItem("getOS"),
    ipAddress: "",
    personId: user?.personId || 0,
    resellerId: user?.subscriberInfo?.managingResellerID || 0,
  };

  if (FLAGS.IS_DEBUG) {
    console.log(`${new Date().toISOString()} | ${ce.personId} | ${ce.resellerId}`);
    console.log(ce.stackTrace);
    console.log(ce.userAgent);
    console.log(JSON.stringify(ce));
  }

  //send it
  await unifiedApi.putClientException(ce);

  //delete the queue of events
  localStorage.removeItem(EVENT_ARRAY);
}

export async function logException(err: Error, extra?: unknown) {
  //check if error is an Axios error and use logAxiosException if it is
  const axiosError = err as AxiosError;
  if (axiosError?.request?.responseURL) {
    logAxiosException(axiosError);
    return;
  }

  const userStore = useUserStore();
  const user = userStore.currentUser;

  const errorMessage = new Date().getTime().toString();
  let extraString = "";
  if (extra) extraString = JSON.stringify(extra);

  let stackTrace = "";
  if (err.stack) stackTrace = JSON.stringify(err.stack.split("\n"));

  //create clientException message
  const ce: IClientException = {
    clientIdentifier: `${CLIENT_IDENTIFIER} ${BUILD_HASH.substring(0, BUILD_HASH_DISPLAY_LENGTH)}`,
    errorMessage,
    cause: localStorage.getItem(EVENT_ARRAY) || "",
    stackTrace,
    url: extraString,
    userAgent: "isMobile: " + !localStorage.getItem("isMobile") + " OS: " + localStorage.getItem("getOS"),
    ipAddress: "",
    personId: user?.personId || 0,
    resellerId: user?.subscriberInfo?.managingResellerID || 0,
  };

  if (FLAGS.IS_DEBUG) {
    console.log(`${new Date().toISOString()} | ${ce.personId} | ${ce.resellerId}`);
    console.log(ce.stackTrace);
    console.log(ce.userAgent);
    if (extra) console.log(extra);
  }

  //send it
  await unifiedApi.putClientException(ce);

  //delete the queue of events
  localStorage.removeItem(EVENT_ARRAY);
}
